import type {Viewer} from '../../../../viewer';
import type {FeatureEditSession} from '../../../edit/FeatureEditSession';
import type {RevolveFeatureDefinitionModel} from '../../../definition/revolve/RevolveFeatureDefinition';
import {RevolveCommandAdapter} from '../../../command/revolve/RevolveCommandAdapter';
import type {RevolveFeaturePrototype} from '../../../prototype/revolve/RevolveFeaturePrototype';
import type {FeatureExecutionRuntime} from '../../FeatureExecutionRuntime';
import {FeatureCommitExecution} from '../FeatureCommitExecution';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/execution/commit/revolve 目录；
 * 2. 负责旋转特征的正式提交；
 * 3. 该执行器统一收口创建提交与编辑替换提交。
 */

/**
 * 旋转特征正式提交执行器。
 */
export class RevolveCommitExecution extends FeatureCommitExecution<
    RevolveFeatureDefinitionModel,
    RevolveFeaturePrototype
> {
    /** 执行运行时。 */
    private readonly runtime: FeatureExecutionRuntime;
    /** 替换命令通道。 */
    private readonly replaceFeatureCommand: (featureId: string, command: CadCommand) => Promise<boolean>;
    /** 命令适配器。 */
    private readonly adapter: RevolveCommandAdapter;

    /**
     * @param _viewer Viewer 实例。
     * @param runtime 执行运行时。
     * @param replaceFeatureCommand 替换命令回调。
     */
    public constructor(
        _viewer: Viewer,
        runtime: FeatureExecutionRuntime,
        replaceFeatureCommand: (featureId: string, command: CadCommand) => Promise<boolean>,
    ) {
        super();
        this.runtime = runtime;
        this.replaceFeatureCommand = replaceFeatureCommand;
        this.adapter = new RevolveCommandAdapter();
    }

    /**
     * @inheritdoc
     */
    public async commitCreate(prototype: RevolveFeaturePrototype): Promise<string[]> {
        const command = this.adapter.createCommandFromPrototype({
            prototype,
            clientId: 'web-client',
        }) as CadCommand;
        const result = await this.runtime.executeCommand(command);
        return Array.isArray(result.objectIds) && result.objectIds.length > 0
            ? result.objectIds
            : [result.objectId];
    }

    /**
     * @inheritdoc
     */
    public async commitEdit(
        session: FeatureEditSession<RevolveFeatureDefinitionModel, RevolveFeaturePrototype>,
        prototype: RevolveFeaturePrototype,
    ): Promise<boolean> {
        const command = this.adapter.createCommandFromPrototype({
            prototype,
            clientId: 'web-client',
        }) as CadCommand;
        return this.replaceFeatureCommand(session.getPrototype().getFeatureId(), command);
    }
}
